import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token.');
      return;
    }

    const verify = async () => {
      try {
        const res = await api.post(`/user/email/verify/${token}`);
        setStatus('success');
        setMessage(res.data.message);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="glass border-white/20 dark:border-white/10">
          <CardHeader className="text-center">
            {status === 'verifying' && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="w-12 h-12 text-primary animate-spin" />
                </div>
                <CardTitle className="font-['Manrope']">Verifying Email</CardTitle>
                <CardDescription>Please wait while we verify your new email address...</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle className="w-12 h-12 text-emerald-500" />
                </div>
                <CardTitle className="font-['Manrope'] text-emerald-600 dark:text-emerald-400">Email Updated</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
            {status === 'error' && (
              <>
                <div className="flex justify-center mb-4">
                  <XCircle className="w-12 h-12 text-destructive" />
                </div>
                <CardTitle className="font-['Manrope'] text-destructive">Verification Failed</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="flex justify-center">
            {status !== 'verifying' && (
              <Button
                onClick={() => navigate(status === 'success' ? '/login' : '/settings')}
                data-testid="verify-email-action-btn"
              >
                {status === 'success' ? 'Log In with New Email' : 'Back to Settings'}
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
